import { format, parse } from 'date-fns';
import { CheckIcon, ChevronsUpDownIcon, PlusIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { type Control, Controller, type UseFormReturn } from 'react-hook-form';

import { LeadPicker } from '@/components/app/lead-picker';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  MOBILE_PRIMARY_BUTTON_CLASS,
  MOBILE_SELECT_TRIGGER_CLASS,
  MOBILE_TEXTAREA_CLASS,
  MobileDateRow,
  MobileSelectionPill,
  MobileTimePickerRow,
} from '@/features/mobile-ui';
import { scrollWhenWheelBlocked } from '@/lib/scroll-when-wheel-blocked';
import { cn } from '@/lib/utils';

import {
  APPOINTMENT_NO_PRACTITIONER,
  type AppointmentCreateFormData,
  type AppointmentCreatePractitioner,
  type AppointmentCreateService,
  appointmentCreateForm,
  practitionersForService,
  resolveAppointmentDurationMinutes,
} from './appointment-create-form';
import type { AdditionalServicesSelection } from './use-additional-services';

/**
 * SHARED FIELDS — every create-appointment surface composes these, bound to the
 * shared `appointmentCreateForm`. `variant` only changes presentation
 * (desktop dialog rows vs. mobile funnel rows); the value written into the form
 * is identical, which is what keeps the form contract honest.
 *
 * Labels are rendered from the declaration (`L.x`), never as literals — the
 * contract's harness locates each control by that same string, so the JSX and
 * the field registry cannot drift apart.
 */
const L = appointmentCreateForm.labels;
export type AppointmentFieldVariant = 'desktop' | 'mobile';

interface FieldProps {
  control: Control<AppointmentCreateFormData>;
  variant?: AppointmentFieldVariant;
}

function formatDisplayDate(dateStr: string): string {
  const parsed = parse(dateStr, 'yyyy-MM-dd', new Date());
  return Number.isNaN(parsed.getTime()) ? dateStr : format(parsed, 'EEE d MMM');
}

/** Client picker (leadId). */
export function AppointmentClientField({ control }: FieldProps) {
  return (
    <Controller
      name="leadId"
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          <FieldLabel htmlFor={field.name}>{L.leadId}</FieldLabel>
          <LeadPicker
            value={field.value}
            onValueChange={(leadId) => field.onChange(leadId)}
            placeholder="Select a client"
          />
          {fieldState.error && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}

/** Service picker (serviceId). Duration + title are derived from it. */
export function AppointmentServiceField({
  control,
  services,
  isLoading,
  variant = 'desktop',
}: FieldProps & {
  services: AppointmentCreateService[];
  isLoading?: boolean;
}) {
  return (
    <Controller
      name="serviceId"
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          <FieldLabel htmlFor={field.name}>{L.serviceId}</FieldLabel>
          <Select
            value={field.value}
            onValueChange={field.onChange}
            disabled={isLoading}
          >
            <SelectTrigger
              id={field.name}
              className={cn(
                variant === 'mobile' && MOBILE_SELECT_TRIGGER_CLASS
              )}
            >
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {services.map((service) => (
                <SelectItem key={service.id} value={service.id}>
                  {service.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {fieldState.error && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}

/**
 * Practitioner picker. Optional: `APPOINTMENT_NO_PRACTITIONER` means the
 * booking is left unassigned — we never silently invent one.
 */
export function AppointmentPractitionerField({
  control,
  practitioners,
  variant = 'desktop',
  description,
}: FieldProps & {
  practitioners: AppointmentCreatePractitioner[];
  /** Which service this is for, once a booking has several. */
  description?: string;
}) {
  return (
    <Controller
      name="practitionerId"
      control={control}
      render={({ field }) => {
        const value = field.value || APPOINTMENT_NO_PRACTITIONER;

        if (variant === 'mobile') {
          return (
            <div className="flex flex-wrap gap-2">
              <MobileSelectionPill
                label="Any team member"
                selected={value === APPOINTMENT_NO_PRACTITIONER}
                onClick={() => field.onChange(APPOINTMENT_NO_PRACTITIONER)}
              />
              {practitioners.map((p) => (
                <MobileSelectionPill
                  key={p.id}
                  label={p.name}
                  selected={value === p.id}
                  onClick={() => field.onChange(p.id)}
                />
              ))}
            </div>
          );
        }

        return (
          <Field>
            <FieldLabel htmlFor={field.name}>{L.practitionerId}</FieldLabel>
            {description && <FieldDescription>{description}</FieldDescription>}
            <Select value={value} onValueChange={field.onChange}>
              <SelectTrigger id={field.name}>
                <SelectValue placeholder="Any team member" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={APPOINTMENT_NO_PRACTITIONER}>
                  Any team member
                </SelectItem>
                {practitioners.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        );
      }}
    />
  );
}

/** Date + start time. End time is always start + the service's duration. */
export function AppointmentDateTimeFields({
  control,
  variant = 'desktop',
}: FieldProps) {
  if (variant === 'mobile') {
    return (
      <section>
        <p className="pb-2 text-[13px] text-[#8E8E93]">When</p>
        <div className="divide-y divide-[#E5E5EA] overflow-hidden rounded-xl border border-[#E5E5EA]">
          <Controller
            name="date"
            control={control}
            render={({ field }) => (
              <MobileDateRow
                label={L.date}
                value={formatDisplayDate(field.value)}
                selected={parse(field.value, 'yyyy-MM-dd', new Date())}
                onSelect={(date) => field.onChange(format(date, 'yyyy-MM-dd'))}
              />
            )}
          />
          <Controller
            name="startTime"
            control={control}
            render={({ field }) => (
              <MobileTimePickerRow
                label={L.startTime}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </section>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <Controller
        name="date"
        control={control}
        render={({ field, fieldState }) => (
          <Field data-invalid={fieldState.invalid}>
            <FieldLabel htmlFor={field.name}>{L.date}</FieldLabel>
            <DatePicker
              id={field.name}
              placeholder="Select date"
              value={
                field.value
                  ? parse(field.value, 'yyyy-MM-dd', new Date())
                  : undefined
              }
              onChange={(date) =>
                field.onChange(date ? format(date, 'yyyy-MM-dd') : '')
              }
            />
            {fieldState.error && <FieldError errors={[fieldState.error]} />}
          </Field>
        )}
      />
      <Controller
        name="startTime"
        control={control}
        render={({ field, fieldState }) => (
          <Field data-invalid={fieldState.invalid}>
            <FieldLabel htmlFor={field.name}>{L.startTime}</FieldLabel>
            <Input {...field} id={field.name} type="time" />
            {fieldState.error && <FieldError errors={[fieldState.error]} />}
          </Field>
        )}
      />
    </div>
  );
}

/** Free-text notes → the appointment's `description`. */
export function AppointmentNotesField({
  control,
  variant = 'desktop',
}: FieldProps) {
  return (
    <Controller
      name="notes"
      control={control}
      render={({ field }) => (
        <Field>
          <FieldLabel htmlFor={field.name}>{L.notes}</FieldLabel>
          <Textarea
            {...field}
            id={field.name}
            placeholder="Enter notes for the appointment"
            rows={3}
            className={cn(variant === 'mobile' && MOBILE_TEXTAREA_CLASS)}
          />
        </Field>
      )}
    />
  );
}

/**
 * Read-only summary of the derived appointment: the title comes from the
 * service and the duration is the service's own length. Rendered so users can
 * see what they're about to book without being asked to type it.
 */
export function AppointmentDerivedSummary({
  service,
  className,
}: {
  service: AppointmentCreateService | undefined;
  className?: string;
}) {
  const duration = resolveAppointmentDurationMinutes(service);
  return (
    <div
      className={cn(
        'rounded-xl border border-[#E5E5EA] bg-white px-3 py-2.5',
        className
      )}
    >
      <p className="text-[13px] text-[#8E8E93]">Appointment</p>
      <p className="text-[15px] font-medium text-black">
        {service?.name ?? 'Appointment'}
      </p>
      <p className="text-[13px] text-[#8E8E93]">{duration} min</p>
    </div>
  );
}

/**
 * The services a desktop booking adds after the first, each with its own team
 * member. The team-member list on each row holds only the people who perform
 * that row's service (or everyone, when nobody is assigned to it).
 *
 * Rows run in order after the first service: the visit is booked back to back
 * from the start time.
 */
export function AppointmentAdditionalServicesField({
  selection,
  services,
  practitioners,
  isLoading,
  defaultPractitionerId,
  variant = 'desktop',
}: {
  selection: AdditionalServicesSelection;
  services: AppointmentCreateService[];
  practitioners: AppointmentCreatePractitioner[];
  isLoading?: boolean;
  /** Preset for a new row: the first service's team member. */
  defaultPractitionerId?: string;
  variant?: AppointmentFieldVariant;
}) {
  if (variant === 'mobile') {
    return (
      <MobileAdditionalServices
        selection={selection}
        services={services}
        practitioners={practitioners}
        isLoading={isLoading}
        defaultPractitionerId={defaultPractitionerId}
      />
    );
  }
  // Desktop: the services are chosen in the Service combobox above, so each
  // added service is just its team member, labelled with the service.
  return (
    <>
      {selection.items.map((item, index) => {
        const position = index + 2;
        const fieldId = `${item.key}-practitioner`;
        const serviceName =
          services.find((service) => service.id === item.serviceId)?.name ??
          `Service ${position}`;
        const eligible = practitionersForService(practitioners, item.serviceId);
        return (
          <Field key={item.key}>
            <FieldLabel htmlFor={fieldId}>{L.practitionerId}</FieldLabel>
            <FieldDescription>{`For ${serviceName}`}</FieldDescription>
            <Select
              value={item.practitionerId || APPOINTMENT_NO_PRACTITIONER}
              onValueChange={(practitionerId) =>
                selection.setPractitioner(item.key, practitionerId)
              }
            >
              <SelectTrigger
                id={fieldId}
                aria-label={`${L.practitionerId} for ${serviceName}`}
                className="w-full"
              >
                <SelectValue placeholder="Any team member" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={APPOINTMENT_NO_PRACTITIONER}>
                  Any team member
                </SelectItem>
                {eligible.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        );
      })}
    </>
  );
}

/**
 * The desktop Service field: a searchable combobox that takes several
 * services. They run in the order they are ticked; the first is the form's
 * own `serviceId` (so a one-service booking is exactly what it always was),
 * the rest are the added services, each with its own team member below.
 *
 * Unticking the first promotes the next one, team member and all.
 */
export function AppointmentServicesCombobox({
  form,
  selection,
  services,
  isLoading,
}: {
  form: UseFormReturn<AppointmentCreateFormData>;
  selection: AdditionalServicesSelection;
  services: AppointmentCreateService[];
  isLoading?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Controller
      name="serviceId"
      control={form.control}
      render={({ field, fieldState }) => {
        const selectedIds = [
          ...(field.value ? [field.value] : []),
          ...selection.items.map((item) => item.serviceId),
        ];
        const nameOf = (id: string) =>
          services.find((service) => service.id === id)?.name ?? 'Service';
        const full = !selection.canAdd;

        const toggle = (id: string) => {
          if (id === field.value) {
            const [next] = selection.items;
            if (next) {
              field.onChange(next.serviceId);
              form.setValue('practitionerId', next.practitionerId);
              selection.remove(next.key);
            } else {
              field.onChange('');
            }
            return;
          }
          const added = selection.items.find((item) => item.serviceId === id);
          if (added) {
            selection.remove(added.key);
            return;
          }
          if (!field.value) {
            field.onChange(id);
            return;
          }
          // A new service starts with the first one's team member, kept only
          // if they perform it.
          selection.addService(id, form.getValues('practitionerId'));
        };

        return (
          <Field data-invalid={fieldState.invalid}>
            <FieldLabel htmlFor={field.name}>{L.serviceId}</FieldLabel>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  id={field.name}
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  aria-invalid={fieldState.invalid}
                  disabled={isLoading}
                  className="h-auto min-h-9 w-full justify-between font-normal"
                >
                  {selectedIds.length === 0 ? (
                    <span className="text-muted-foreground">
                      Select services
                    </span>
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {selectedIds.map((id, index) => (
                        <Badge key={id} variant="secondary">
                          {selectedIds.length > 1 ? `${index + 1}. ` : ''}
                          {nameOf(id)}
                        </Badge>
                      ))}
                    </span>
                  )}
                  <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[var(--radix-popover-trigger-width)] p-0"
              >
                <Command>
                  <CommandInput placeholder="Search services..." />
                  {/* The dialog's scroll lock would swallow the wheel here. */}
                  <CommandList onWheel={scrollWhenWheelBlocked}>
                    <CommandEmpty>No services found.</CommandEmpty>
                    <CommandGroup>
                      {services.map((service) => {
                        const checked = selectedIds.includes(service.id);
                        return (
                          <CommandItem
                            key={service.id}
                            value={`${service.name} ${service.id}`}
                            disabled={!checked && full}
                            onSelect={() => toggle(service.id)}
                          >
                            <CheckIcon
                              className={cn(
                                'size-4',
                                checked ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            {service.name}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {selectedIds.length > 1 && (
              <FieldDescription>
                Booked back to back, in this order.
              </FieldDescription>
            )}
            {fieldState.error && <FieldError errors={[fieldState.error]} />}
          </Field>
        );
      }}
    />
  );
}

/**
 * The mobile funnels' take on the same rows: one card per added service, a
 * service picker in the funnel's select style and the team member as pills,
 * like the first service's.
 */
function MobileAdditionalServices({
  selection,
  services,
  practitioners,
  isLoading,
  defaultPractitionerId,
}: {
  selection: AdditionalServicesSelection;
  services: AppointmentCreateService[];
  practitioners: AppointmentCreatePractitioner[];
  isLoading?: boolean;
  defaultPractitionerId?: string;
}) {
  return (
    <section className="flex flex-col gap-3">
      {selection.items.map((item, index) => {
        const position = index + 2;
        const serviceFieldId = `${item.key}-service`;
        const error = selection.errors[item.key];
        const eligible = practitionersForService(practitioners, item.serviceId);
        const value = item.practitionerId || APPOINTMENT_NO_PRACTITIONER;
        return (
          <div
            key={item.key}
            role="group"
            aria-label={`Service ${position}`}
            className="flex flex-col gap-3 rounded-xl border border-[#E5E5EA] p-3"
          >
            <div className="flex items-center justify-between">
              <label
                htmlFor={serviceFieldId}
                className={cn(
                  'text-[13px] text-[#8E8E93]',
                  error && 'text-destructive'
                )}
              >
                {`${L.serviceId} ${position}`}
              </label>
              <button
                type="button"
                aria-label={`Remove service ${position}`}
                onClick={() => selection.remove(item.key)}
                className="-m-1 rounded-full p-1 text-[#8E8E93] active:bg-[#F2F2F7]"
              >
                <XIcon className="size-4" />
              </button>
            </div>
            <Select
              value={item.serviceId}
              onValueChange={(serviceId) =>
                selection.setService(item.key, serviceId)
              }
              disabled={isLoading}
            >
              <SelectTrigger
                id={serviceFieldId}
                aria-invalid={Boolean(error)}
                className={MOBILE_SELECT_TRIGGER_CLASS}
              >
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {services.map((service) => (
                  <SelectItem key={service.id} value={service.id}>
                    {service.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {error && <FieldError errors={[{ message: error }]} />}
            <div
              role="group"
              aria-label={`${L.practitionerId} for service ${position}`}
              className="flex flex-wrap gap-2"
            >
              <MobileSelectionPill
                label="Any team member"
                selected={value === APPOINTMENT_NO_PRACTITIONER}
                onClick={() =>
                  selection.setPractitioner(
                    item.key,
                    APPOINTMENT_NO_PRACTITIONER
                  )
                }
              />
              {eligible.map((p) => (
                <MobileSelectionPill
                  key={p.id}
                  label={p.name}
                  selected={value === p.id}
                  onClick={() => selection.setPractitioner(item.key, p.id)}
                />
              ))}
            </div>
          </div>
        );
      })}
      {selection.canAdd && (
        <button
          type="button"
          onClick={() => selection.add(defaultPractitionerId)}
          className={cn(
            MOBILE_PRIMARY_BUTTON_CLASS,
            'flex items-center justify-center gap-2 border border-black bg-white text-black'
          )}
        >
          <PlusIcon className="size-4" />
          Add another service
        </button>
      )}
    </section>
  );
}
